import { ThemeColor, ThemeMode } from '../types';

export interface ColorSchemeConfig {
  id: ThemeColor;
  label: string;
  badge: string;
  primary: string;
  bgLight: string;
  bgLightTonal: string;
  bgDarkTonal: string;
  textLight: string;
  textDark: string;
  accentBorder: string;
  glow: string;
  fabBg: string;
}

export const COLOR_SCHEMES: Record<ThemeColor, ColorSchemeConfig> = {
  violet: {
    id: 'violet',
    label: 'Aura Mesh',
    badge: 'bg-[#7C3AED]',
    primary: '[#7C3AED]',
    bgLight: 'bg-[#7C3AED]',
    bgLightTonal: 'bg-violet-50',
    bgDarkTonal: 'dark:bg-[#161625]',
    textLight: 'text-violet-950',
    textDark: 'dark:text-[#F0F0F5]',
    accentBorder: 'border-[#7C3AED]/40',
    glow: 'rgba(124, 58, 237, 0.4)',
    fabBg: 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white shadow-md',
  },
  amber: {
    id: 'amber',
    label: 'Sunset Glow',
    badge: 'bg-amber-500',
    primary: 'amber-500',
    bgLight: 'bg-amber-500',
    bgLightTonal: 'bg-amber-50',
    bgDarkTonal: 'dark:bg-[#20170A]',
    textLight: 'text-amber-950',
    textDark: 'dark:text-[#F0F0F5]',
    accentBorder: 'border-amber-500/40',
    glow: 'rgba(245, 158, 11, 0.4)',
    fabBg: 'bg-amber-600 hover:bg-amber-500 text-white shadow-md',
  },
  cyan: {
    id: 'cyan',
    label: 'Ocean Calm',
    badge: 'bg-cyan-500',
    primary: 'cyan-500',
    bgLight: 'bg-cyan-500',
    bgLightTonal: 'bg-cyan-50',
    bgDarkTonal: 'dark:bg-[#0E1F26]',
    textLight: 'text-cyan-950',
    textDark: 'dark:text-[#F0F0F5]',
    accentBorder: 'border-cyan-500/40',
    glow: 'rgba(6, 182, 212, 0.4)',
    fabBg: 'bg-cyan-600 hover:bg-cyan-500 text-white shadow-md',
  },
  emerald: {
    id: 'emerald',
    label: 'Sage Forest',
    badge: 'bg-emerald-500',
    primary: 'emerald-500',
    bgLight: 'bg-emerald-500',
    bgLightTonal: 'bg-emerald-50',
    bgDarkTonal: 'dark:bg-[#10221A]',
    textLight: 'text-emerald-950',
    textDark: 'dark:text-[#F0F0F5]',
    accentBorder: 'border-emerald-500/40',
    glow: 'rgba(16, 185, 129, 0.4)',
    fabBg: 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-md',
  },
  rose: {
    id: 'rose',
    label: 'Midnight Velvet',
    badge: 'bg-rose-500',
    primary: 'rose-500',
    bgLight: 'bg-rose-500',
    bgLightTonal: 'bg-rose-50',
    bgDarkTonal: 'dark:bg-[#24121A]',
    textLight: 'text-rose-950',
    textDark: 'dark:text-[#F0F0F5]',
    accentBorder: 'border-rose-500/40',
    glow: 'rgba(244, 63, 94, 0.4)',
    fabBg: 'bg-rose-600 hover:bg-rose-500 text-white shadow-md',
  },
  slate: {
    id: 'slate',
    label: 'Material Clean',
    badge: 'bg-slate-500',
    primary: 'slate-500',
    bgLight: 'bg-slate-600',
    bgLightTonal: 'bg-slate-100',
    bgDarkTonal: 'dark:bg-[#1A1A24]',
    textLight: 'text-slate-950',
    textDark: 'dark:text-[#F0F0F5]',
    accentBorder: 'border-slate-500/40',
    glow: 'rgba(100, 116, 139, 0.4)',
    fabBg: 'bg-slate-700 hover:bg-slate-600 text-white shadow-md',
  },
};

/**
 * Determine whether dark mode should be active based on mode setting:
 * - 'light' -> false
 * - 'dark' -> true
 * - 'auto' -> prefers-color-scheme OR current time is between 20:00 and 07:00
 */
export function isDarkModeActive(mode: ThemeMode): boolean {
  if (mode === 'dark') return true;
  if (mode === 'light') return false;

  // Auto mode
  if (typeof window !== 'undefined') {
    const isSystemDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (isSystemDark) return true;

    // Fallback: sunset to sunrise (20:00 to 07:00)
    const currentHour = new Date().getHours();
    if (currentHour >= 20 || currentHour < 7) {
      return true;
    }
  }
  return false;
}
